/**
 * LogEntry.ts - LogEntry Model
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { generateId } from '@opcentral/core';

export const LogLevel = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  FATAL: 5
};

export const LogLevelNames = {
  0: 'TRACE',
  1: 'DEBUG',
  2: 'INFO',
  3: 'WARN',
  4: 'ERROR',
  5: 'FATAL'
};


interface LogEntryData {
  id?: string;
  timestamp?: number;
  level?: any;
  message?: string;
  category?: any;
  userId?: any;
  sessionId?: any;
  context?: any;
}

export class LogEntry {
  constructor(data: LogEntryData = {}) {
    this.id = data.id || generateId();
    this.timestamp = data.timestamp || new Date().toISOString();
    this.level = data.level !== undefined ? data.level : LogLevel.INFO;
    this.levelName = LogLevelNames[this.level];
    this.message = data.message || '';
    this.category = data.category || 'general'; // Module/category
    this.userId = data.userId || null;
    this.sessionId = data.sessionId || null;
    this.context = data.context || {}; // Additional context
    this.error = data.error || null; // Error object if applicable
    this.stack = data.stack || null; // Stack trace
    this.tags = data.tags || []; // Tags for filtering
    this.metadata = data.metadata || {}; // Additional metadata
  }
  
  /**
   * Check if log level is enabled
   */
  isLevelEnabled(minLevel): boolean {
    return this.level >= minLevel;
  }
  
  /**
   * Format for console output
   */
  toConsoleString(): any {
    const timestamp = new Date(this.timestamp).toISOString();
    const level = this.levelName.padEnd(5);
    const category = this.category.padEnd(15);
    return `[${timestamp}] ${level} [${category}] ${this.message}`;
  }
  
  /**
   * Format for syslog (RFC 5424)
   */
  toSyslogString(): any {
    // <priority>version timestamp hostname app-name procid msgid structured-data msg
    const priority = this.getSyslogPriority();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const appName = 'opcentral';
    const procId = this.sessionId || '-';
    const msgId = this.category;
    
    return `<${priority}>1 ${this.timestamp} ${hostname} ${appName} ${procId} ${msgId} - ${this.message}`;
  }
  
  /**
   * Get syslog priority
   */
  getSyslogPriority(): string {
    // Facility: user (1) = 8
    // Severity mapping: TRACE=7, DEBUG=7, INFO=6, WARN=4, ERROR=3, FATAL=0
    const facility = 1;
    const severityMap = {
      [LogLevel.TRACE]: 7,
      [LogLevel.DEBUG]: 7,
      [LogLevel.INFO]: 6,
      [LogLevel.WARN]: 4,
      [LogLevel.ERROR]: 3,
      [LogLevel.FATAL]: 0
    };
    const severity = severityMap[this.level] || 6;
    return (facility * 8) + severity;
  }
  
  /**
   * Format for JSON logging
   */
  toJSON(): LogEntryData {
    return {
      id: this.id,
      timestamp: this.timestamp,
      level: this.level,
      levelName: this.levelName,
      message: this.message,
      category: this.category,
      userId: this.userId,
      sessionId: this.sessionId,
      context: this.context,
      error: this.error ? {
        name: this.error.name,
        message: this.error.message,
        stack: this.error.stack
      } : null,
      stack: this.stack,
      tags: this.tags,
      metadata: this.metadata
    };
  }
  
  /**
   * Format for structured logging (ELK, Splunk, etc.)
   */
  toStructuredLog(): any {
    return {
      '@timestamp': this.timestamp,
      level: this.levelName,
      message: this.message,
      logger: this.category,
      user_id: this.userId,
      session_id: this.sessionId,
      tags: this.tags,
      ...this.context,
      ...this.metadata,
      ...(this.error ? {
        error: {
          type: this.error.name,
          message: this.error.message,
          stack_trace: this.error.stack
        }
      } : {})
    };
  }
  
  static fromJSON(json: LogEntryData): LogEntry {
    return new LogEntry(json);
  }
}

export default LogEntry;
